/**
 * Cobros que se repiten, vistos por comercio (Altamar · «Pagos fijos con
 * Gavi»). Sin banco, Gavi solo ve lo que se apunta o se importa: con eso
 * agrupa los cargos de cada comercio, separa los que no tienen nada que ver
 * (la luz y el mantenimiento de 7,90 € de la misma compañía), deduce cada
 * cuánto se cobra y mira si ha subido. Funciones puras.
 */
import type { MesesCobro } from '#components/mobile/budget/pagosFijos';
import {
  diasEntre,
  redondearMesesCobro,
} from '#components/mobile/budget/pagosFijos';

import { normalizarComercio } from './tiposRecibo';

/** Un cargo (salida) del historial, con su comercio. */
export type CargoGavi = {
  id: string;
  /** `YYYY-MM-DD`. */
  fecha: string;
  /** Céntimos, en positivo. */
  importe: number;
  payee: string | null;
  comercio: string | null;
  categoria: string | null;
  cuenta: string | null;
};

/** Los cargos de un comercio que se repiten con un mismo ritmo. */
export type SerieCobro = {
  /** Id del comercio; con `#2`, `#3`… si cobra dos cosas distintas. */
  clave: string;
  /** El comercio del primer cargo (el que da la clave). */
  payee: string;
  /** Todos los comercios de la serie: el banco no siempre escribe igual. */
  payees: readonly string[];
  comercio: string;
  /** La categoría donde más veces se ha apuntado. */
  categoria: string | null;
  cuenta: string | null;
  /** Cada cuántos meses se cobra. */
  cada: MesesCobro;
  /** Del más antiguo al más reciente. */
  cargos: readonly { id: string; fecha: string; importe: number }[];
  /** Importe del último cargo (céntimos). */
  importe: number;
  /** Día del mes en que suele cobrarse (mediana). */
  dia: number;
  /** Primer cargo que se conoce (`YYYY-MM-DD`). */
  desde: string;
  /** Fecha prevista del siguiente cargo. */
  siguiente: string;
  /** Lo que cuesta al año a este ritmo (céntimos). */
  anual: number;
};

/** Días medios de un mes. */
const DIAS_MES = 30.44;

/** Meses de historial que mira Gavi. */
export const MESES_HISTORIAL_GAVI = 25;

/** Dos importes vecinos más separados que esto son cosas distintas. */
const SALTO_IMPORTE = 1.6;

/**
 * Suma `n` meses a una fecha `YYYY-MM-DD`, sin pasarse del último día del
 * mes (31 de enero + 1 mes = 28 o 29 de febrero).
 */
export function sumarMeses(fecha: string, n: number): string {
  const [y, m, d] = fecha.split('-').map(Number);
  const total = y * 12 + (m - 1) + n;
  const anio = Math.floor(total / 12);
  const mes = (total % 12) + 1;
  const ultimo = new Date(Date.UTC(anio, mes, 0)).getUTCDate();
  return `${anio}-${String(mes).padStart(2, '0')}-${String(
    Math.min(d, ultimo),
  ).padStart(2, '0')}`;
}

export function sumarDias(fecha: string, n: number): string {
  const ms = Date.parse(`${fecha}T00:00:00Z`) + n * 86_400_000;
  return new Date(ms).toISOString().slice(0, 10);
}

function mediana(valores: readonly number[]): number {
  const v = [...valores].sort((a, b) => a - b);
  const mitad = Math.floor(v.length / 2);
  return v.length % 2 ? v[mitad] : (v[mitad - 1] + v[mitad]) / 2;
}

function masFrecuente<T>(valores: readonly T[]): T | null {
  const cuenta = new Map<T, number>();
  let mejor: T | null = null;
  let max = 0;
  for (const v of valores) {
    const n = (cuenta.get(v) ?? 0) + 1;
    cuenta.set(v, n);
    if (n > max) {
      max = n;
      mejor = v;
    }
  }
  return mejor;
}

/**
 * Parte los cargos de un comercio por importe: ordenados de menor a mayor,
 * un salto de más de ×1,6 entre dos vecinos abre otro grupo. Así la luz
 * (55-78 €) y su mantenimiento (7,90 €) quedan separados, pero una luz que
 * va de 40 a 110 € sigue junta.
 */
export function agruparPorImporte<T extends { importe: number }>(
  cargos: readonly T[],
): T[][] {
  const orden = [...cargos].sort((a, b) => a.importe - b.importe);
  const grupos: T[][] = [];
  for (const c of orden) {
    const actual = grupos[grupos.length - 1];
    const anterior = actual?.[actual.length - 1];
    if (
      anterior &&
      c.importe <= Math.max(anterior.importe, 1) * SALTO_IMPORTE
    ) {
      actual.push(c);
    } else {
      grupos.push([c]);
    }
  }
  return grupos;
}

/**
 * Cada cuántos meses se repiten unas fechas (ordenadas). Mira las 6 últimas
 * separaciones: al menos dos tercios deben caer en el mismo ritmo (± 8 días
 * o ± 15 %). Hacen falta 3 cargos (2 si es anual). `null` si no hay ritmo.
 */
export function ritmoDeFechas(fechas: readonly string[]): MesesCobro | null {
  const separaciones: number[] = [];
  for (let i = 1; i < fechas.length; i++) {
    separaciones.push(diasEntre(fechas[i - 1], fechas[i]));
  }
  const ultimas = separaciones.slice(-6);
  if (ultimas.length === 0) {
    return null;
  }
  const cada = redondearMesesCobro(mediana(ultimas) / DIAS_MES);
  if (ultimas.length < (cada === 12 ? 1 : 2)) {
    return null;
  }
  const esperado = cada * DIAS_MES;
  const margen = Math.max(8, esperado * 0.15);
  const encajan = ultimas.filter(s => Math.abs(s - esperado) <= margen).length;
  return encajan * 3 >= ultimas.length * 2 ? cada : null;
}

/** Palabras que no distinguen un comercio de otro. */
const PALABRAS_VACIAS = new Set([
  'de',
  'del',
  'la',
  'el',
  'los',
  'las',
  'y',
  'i',
  'e',
  'd',
  'l',
  'en',
  'the',
  'of',
  'and',
]);

/**
 * Con qué se agrupan los cargos de un mismo comercio: las dos primeras
 * palabras del nombre, normalizado. El banco escribe «MGC INSURANCE MUT» un
 * mes y «Mgc Insurance Mutua De Seguros…» el siguiente, y Rumbo los guarda
 * como comercios distintos; así vuelven a ser uno. Sin nombre, el id.
 */
export function claveComercio(nombre: string | null, payee: string): string {
  const palabras = normalizarComercio(nombre ?? '')
    .trim()
    .split(' ')
    .filter(p => p && !PALABRAS_VACIAS.has(p));
  return palabras.length > 0 ? palabras.slice(0, 2).join(' ') : `#${payee}`;
}

/**
 * Las series de cobros que se repiten y siguen vivas (el último cargo no
 * se ha saltado más de un turno). Solo cargos con comercio. Dos cobros el
 * mismo día cuentan los dos, pero el ritmo sale de los días.
 */
export function detectarSeries(
  cargos: readonly CargoGavi[],
  hoy: string,
): SerieCobro[] {
  const porComercio = new Map<string, CargoGavi[]>();
  for (const c of cargos) {
    if (!c.payee || c.importe <= 0) {
      continue;
    }
    const clave = claveComercio(c.comercio, c.payee);
    const lista = porComercio.get(clave) ?? [];
    lista.push(c);
    porComercio.set(clave, lista);
  }

  const series: SerieCobro[] = [];
  for (const lista of porComercio.values()) {
    // El grupo de más dinero primero: es el recibo «de verdad».
    const grupos = agruparPorImporte(lista).sort(
      (a, b) => mediana(b.map(c => c.importe)) - mediana(a.map(c => c.importe)),
    );
    // La clave sale del comercio del primer cargo conocido: no cambia
    // aunque el banco cambie el nombre más adelante.
    const payee = [...lista].sort((a, b) => a.fecha.localeCompare(b.fecha))[0]
      .payee as string;
    let n = 0;
    for (const grupo of grupos) {
      const orden = [...grupo].sort((a, b) => a.fecha.localeCompare(b.fecha));
      const cada = ritmoDeFechas([...new Set(orden.map(c => c.fecha))]);
      if (cada == null) {
        continue;
      }
      const ultimo = orden[orden.length - 1];
      const siguiente = sumarMeses(ultimo.fecha, cada);
      if (diasEntre(siguiente, hoy) > Math.max(20, cada * 6)) {
        continue;
      }
      n += 1;
      const recientes = orden.slice(-6);
      series.push({
        clave: n === 1 ? payee : `${payee}#${n}`,
        payee,
        payees: [...new Set(orden.map(c => c.payee as string))],
        comercio: masFrecuente(orden.map(c => c.comercio ?? '')) || '',
        categoria: masFrecuente(orden.map(c => c.categoria)),
        cuenta: masFrecuente(orden.map(c => c.cuenta)),
        cada,
        cargos: orden.map(({ id, fecha, importe }) => ({ id, fecha, importe })),
        importe: ultimo.importe,
        dia: Math.round(mediana(recientes.map(c => Number(c.fecha.slice(8))))),
        desde: orden[0].fecha,
        siguiente,
        anual: Math.round((mediana(recientes.map(c => c.importe)) * 12) / cada),
      });
    }
  }
  return series.sort((a, b) => b.anual - a.anual);
}

/** Dos importes iguales a ±1 %. */
function igual(a: number, b: number): boolean {
  return Math.abs(a - b) <= Math.max(a, b) * 0.01;
}

export type Subida = {
  antes: number;
  ahora: number;
  /** Primer cargo con el precio nuevo. */
  fecha: string;
};

/**
 * Subida de un recibo de importe fijo: el precio de ahora (los últimos
 * cargos iguales) frente al de antes. `null` si no ha subido o si el
 * importe baila (luz, gas: eso va por `comparaConHaceUnAnio`).
 */
export function subidaDe(serie: SerieCobro): Subida | null {
  const c = serie.cargos;
  if (c.length < 2) {
    return null;
  }
  const ahora = c[c.length - 1].importe;
  let i = c.length - 1;
  while (i > 0 && igual(c[i - 1].importe, ahora)) {
    i -= 1;
  }
  if (i === 0) {
    return null;
  }
  const antes = c[i - 1].importe;
  // Antes también era un precio fijo (dos cargos iguales), salvo en los
  // anuales, que tienen pocos cargos.
  if (serie.cada < 12 && i >= 2 && !igual(c[i - 2].importe, antes)) {
    return null;
  }
  if (ahora <= antes) {
    return null;
  }
  return { antes, ahora, fecha: c[i].fecha };
}

/** ¿La subida merece decirse? (+2 % o +1 €). */
export function subidaNotable(s: Subida): boolean {
  return s.ahora - s.antes >= 100 || s.ahora >= s.antes * 1.02;
}

export type Interanual = {
  /** Media por cargo de los últimos 6 meses y de los mismos meses de hace un año. */
  ahora: number;
  antes: number;
  /** +0,18 = un 18 % más. */
  cambio: number;
  /** Diferencia al año, a este ritmo (céntimos). */
  alAnio: number;
};

/**
 * Luz y gas: lo que se paga en los últimos 6 meses frente a los mismos 6
 * meses de hace un año (media por cargo). Necesita al menos 2 cargos en
 * cada tramo. Pagar más puede ser gastar más: quien lo cuenta pregunta.
 */
export function comparaConHaceUnAnio(
  serie: SerieCobro,
  hoy: string,
): Interanual | null {
  const inicio = sumarMeses(hoy, -6);
  const inicioAntes = sumarMeses(hoy, -18);
  const finAntes = sumarMeses(hoy, -12);
  const ahora = serie.cargos.filter(c => c.fecha > inicio && c.fecha <= hoy);
  const antes = serie.cargos.filter(
    c => c.fecha > inicioAntes && c.fecha <= finAntes,
  );
  if (ahora.length < 2 || antes.length < 2) {
    return null;
  }
  const media = (l: readonly { importe: number }[]) =>
    l.reduce((s, c) => s + c.importe, 0) / l.length;
  const a = media(ahora);
  const b = media(antes);
  if (b <= 0) {
    return null;
  }
  return {
    ahora: Math.round(a),
    antes: Math.round(b),
    cambio: a / b - 1,
    alAnio: Math.round(((a - b) * 12) / serie.cada),
  };
}

/** Meses enteros desde `desde` hasta `hoy`. */
export function mesesDesde(desde: string, hoy: string): number {
  return Math.floor(diasEntre(desde, hoy) / DIAS_MES);
}

/**
 * Próximo aniversario de una fecha a partir de hoy (incluido): el día en
 * que se renueva algo que empezó o se cobró entonces.
 */
export function proximoAniversario(fecha: string, hoy: string): string {
  let f = fecha;
  while (f < hoy) {
    f = sumarMeses(f, 12);
  }
  return f;
}

/**
 * Primer cargo conocido de cada cuenta en el historial cargado: sirve para
 * no confundir «empezó hace 11 meses» con «el historial empieza ahí».
 */
export function inicioPorCuenta(
  cargos: readonly CargoGavi[],
): Map<string, string> {
  const inicio = new Map<string, string>();
  for (const c of cargos) {
    if (!c.cuenta) {
      continue;
    }
    const actual = inicio.get(c.cuenta);
    if (!actual || c.fecha < actual) {
      inicio.set(c.cuenta, c.fecha);
    }
  }
  return inicio;
}
